import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
} from "@mui/material";
import { tokens } from "./tokens";
import ClinicalEmptyState from "./ClinicalEmptyState";

/**
 * Data Grid clínico (§5.2) — tabla densa: filas 36-40px, header sticky en
 * surface-2, filas alternas, hover sutil, fila seleccionada con borde
 * izquierdo primary 3px. Columnas de código en mono, cifras a la derecha.
 *
 * Componente puramente presentacional: recibe las `rows` YA ordenadas/filtradas.
 * El orden y el filtrado se resuelven aguas arriba (ver, p. ej., los controles
 * QueueSortControl / QueueFilterControl del módulo previsit-planning).
 *
 * @param {Object[]} columns
 * @param {string}   columns[].key      - key del row o id de columna
 * @param {string}   columns[].header   - texto del header
 * @param {"left"|"right"|"center"} [columns[].align]
 * @param {boolean}  [columns[].mono]   - renderiza el contenido en fuente mono
 * @param {number|string} [columns[].width]
 * @param {Function} [columns[].render] - (row) => ReactNode
 * @param {Object[]} rows
 * @param {Function} [rowKey]           - (row, index) => string
 * @param {Function} [onRowClick]       - (row) => void
 * @param {Function} [isRowSelected]    - (row) => boolean
 * @param {boolean}  [loading]
 * @param {string}   [emptyMessage]
 * @param {number}   [maxHeight]
 */
export default function ClinicalDataGrid({
  columns = [],
  rows = [],
  rowKey = (row, i) => row.id ?? i,
  onRowClick,
  isRowSelected,
  loading = false,
  emptyMessage = "No records match the current filters.",
  maxHeight = 560,
  sx,
}) {
  return (
    <TableContainer
      sx={{
        bgcolor: tokens.surface,
        border: `1px solid ${tokens.line}`,
        borderRadius: "6px",
        maxHeight,
        ...sx,
      }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell
                key={c.key}
                align={c.align || "left"}
                sx={{
                  bgcolor: tokens.surface2,
                  borderBottom: `1px solid ${tokens.lineStrong}`,
                  fontFamily: tokens.fontUI,
                  fontSize: 11.5,
                  fontWeight: 600,
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  color: tokens.ink2,
                  py: 0.75,
                  whiteSpace: "nowrap",
                  width: c.width,
                }}
              >
                {c.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading &&
            Array.from({ length: 8 }).map((_, i) => (
              <TableRow key={`sk-${i}`}>
                {columns.map((c) => (
                  <TableCell key={c.key} sx={{ py: 0.9 }}>
                    <Skeleton variant="text" width="80%" />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          {!loading && rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length} sx={{ border: 0, py: 4 }}>
                <ClinicalEmptyState message={emptyMessage} />
              </TableCell>
            </TableRow>
          )}
          {!loading &&
            rows.map((row, i) => {
              const selected = isRowSelected ? isRowSelected(row) : false;
              return (
                <TableRow
                  key={rowKey(row, i)}
                  hover={Boolean(onRowClick)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={{
                    cursor: onRowClick ? "pointer" : "default",
                    bgcolor: selected
                      ? tokens.primary50
                      : i % 2 === 1
                      ? tokens.surface2
                      : tokens.surface,
                    "& td:first-of-type": {
                      borderLeft: selected
                        ? `3px solid ${tokens.primary}`
                        : "3px solid transparent",
                    },
                    "&:hover": onRowClick ? { bgcolor: tokens.primary50 } : undefined,
                  }}
                >
                  {columns.map((c) => (
                    <TableCell
                      key={c.key}
                      align={c.align || "left"}
                      sx={{
                        py: 0.7,
                        fontFamily: c.mono ? tokens.fontMono : tokens.fontUI,
                        fontVariantNumeric: "tabular-nums",
                        fontSize: 13,
                        color: tokens.ink,
                        borderBottom: `1px solid ${tokens.line}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {c.render ? c.render(row) : row[c.key]}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
